import type { ApiErrorEnvelope } from "@m8t-stack/api-contract";
import type { FoundryErrorCategory } from "@m8t-stack/foundry-invoke";

/**
 * Thrown for failures that originate locally (no `az login`, gateway
 * discovery zero matches, etc.) — distinguished from `ApiCallError` which
 * carries a backend envelope.
 *
 * `code` is a stable string the hint table maps to a user-facing hint. It's
 * a deliberately separate namespace from backend `ApiError.code`s so local
 * + remote codes never collide.
 */
export class LocalCliError extends Error {
  readonly code: string;
  readonly hint?: string;
  readonly cause?: unknown;
  readonly category?: FoundryErrorCategory;
  readonly retryable?: boolean;

  constructor(opts: { code: string; message: string; hint?: string; cause?: unknown; category?: FoundryErrorCategory; retryable?: boolean }) {
    super(opts.message);
    this.name = "LocalCliError";
    this.code = opts.code;
    this.hint = opts.hint;
    this.cause = opts.cause;
    this.category = opts.category;
    this.retryable = opts.retryable;
  }
}

/**
 * Thrown by `apiCall<T>` (Task 6) when the backend returned an `ok:false`
 * envelope. Carries the parsed envelope unchanged so the CLI can render
 * hints and surface details on --verbose.
 */
export class ApiCallError extends Error {
  readonly envelope: ApiErrorEnvelope;
  readonly httpStatus: number;

  constructor(envelope: ApiErrorEnvelope, httpStatus: number) {
    super(envelope.message);
    this.name = "ApiCallError";
    this.envelope = envelope;
    this.httpStatus = httpStatus;
  }
}
